import React, { useState, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// ─── Theme ──────────────────────────────────────────────────────────────────────
// NOTE: backdropFilter: 'blur(10px)' was removed. backdrop-filter forces the GPU
// to re-sample and re-blur everything behind the Paper on every repaint (typing,
// focus rings, snackbar animation, spinner). Combined with the full-page gradient
// background, this was the single biggest source of perceived lag. Paper opacity
// is bumped up slightly to compensate visually for the lost blur.
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#e2b77e' },
    background: {
      default: '#121212',
      paper: 'rgba(30, 30, 30, 0.92)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600, letterSpacing: '0.05em' },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '12px 24px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
  },
});

// ─── Constants ──────────────────────────────────────────────────────────────────
const PROPERTIES = [
  { id: 1, name: 'Hampton Inn Akron Fairlawn' },
  { id: 2, name: 'Hampton Inn Youngstown West I-80' },
];

const ROOM_TYPES = ['2 Queen Beds', '1 King Bed', '1 King Suite'];

// ─── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  pageBackground: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
    p: 3,
  },
  card: {
    p: { xs: 3, md: 5 },
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
  },
  goldBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 6,
    background: 'linear-gradient(90deg, #e2b77e, #b8860b)',
  },
  formGrid: {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  },
  fullSpan: {
    gridColumn: '1 / -1',
  },
  threeCol: {
    display: 'grid',
    gap: 3,
    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
    gridColumn: '1 / -1',
  },
  chip: {
    backgroundColor: 'rgba(226, 183, 126, 0.15)',
    color: '#e2b77e',
    borderRadius: 1,
    fontSize: '0.75rem',
  },
  checkbox: {
    color: 'rgba(226, 183, 126, 0.5)',
    '&.Mui-checked': { color: '#e2b77e' },
  },
  spinner: {
    color: 'primary.main',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: '-12px',
    marginLeft: '-12px',
  },
  headerBox: {
    textAlign: 'center',
    mb: 4,
  },
  submitButton: {
    gridColumn: '1 / -1',
    mt: 1,
    position: 'relative',
  },
  alert: {
    width: '100%',
  },
};

// ─── Render-stable props/elements ──────────────────────────────────────────────
// These don't depend on component state, so they're hoisted to module scope.
// Passing them inline (e.g. inputProps={{ min: 1, max: 9999 }}) would create a
// brand-new object every render, which breaks memo() comparisons on children
// even when nothing relevant actually changed.
const roomCountInputProps = { min: 1, max: 9999 };
const datePickerSlotProps = { textField: { fullWidth: true, required: true } };
const roomTypeInputElement = <OutlinedInput label="Room Type(s)" />;
const snackbarAnchorOrigin = { vertical: 'bottom', horizontal: 'center' };

// ─── Memoized field components ─────────────────────────────────────────────────
// IMPORTANT: these are defined OUTSIDE SalesLeadForm, at module scope. If they
// were defined inside the component function, a new component "type" would be
// created on every render and memo() would have no effect at all.

const MemoTextField = memo(function MemoTextField(props) {
  return <TextField {...props} />;
});

const MemoDatePicker = memo(function MemoDatePicker(props) {
  return <DatePicker {...props} />;
});

const RoomTypeSelect = memo(function RoomTypeSelect({ value, onChange }) {
  const renderSelected = useCallback(
    (selected) => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((val) => (
          <Chip key={val} label={val} size="small" sx={styles.chip} />
        ))}
      </Box>
    ),
    []
  );

  return (
    <FormControl fullWidth>
      <InputLabel id="room-type-label">Room Type(s)</InputLabel>
      <Select
        labelId="room-type-label"
        id="room-type-select"
        multiple
        value={value}
        onChange={onChange}
        input={roomTypeInputElement}
        renderValue={renderSelected}
      >
        {ROOM_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            <Checkbox checked={value.includes(type)} sx={styles.checkbox} />
            <ListItemText primary={type} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

// ─── Component ──────────────────────────────────────────────────────────────────
export default function SalesLeadForm() {
  const [formData, setFormData] = useState({
    propertyId: '',
    name: '',
    email: '',
    phone: '',
    checkInDate: null,
    checkOutDate: null,
    numberOfRooms: '',
    roomTypes: [],
    message: '',
  });

  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Handlers ────────────────────────────────────────────────────────────────
  // All of these are useCallback'd with stable (empty or stable-ref) deps, so
  // they keep the same identity across renders — required for memo()'d children
  // to actually skip re-rendering.
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDateChange = useCallback((name, newValue) => {
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  }, []);

  // Previously these were inline arrows passed directly to onChange, e.g.
  // onChange={(v) => handleDateChange('checkInDate', v)} — a new function on
  // every render, which would have defeated memoizing the DatePickers.
  const handleCheckInChange = useCallback(
    (v) => handleDateChange('checkInDate', v),
    [handleDateChange]
  );
  const handleCheckOutChange = useCallback(
    (v) => handleDateChange('checkOutDate', v),
    [handleDateChange]
  );

  const handleRoomTypeChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      roomTypes: typeof value === 'string' ? value.split(',') : value,
    }));
  }, []);

  // dayjs() returns a new object every call, so calling it inline as minDate
  // would change identity every render. Memoize "today" once, and only
  // recompute the check-out minimum when check-in actually changes.
  const today = useMemo(() => dayjs(), []);
  const checkOutMinDate = useMemo(
    () => (formData.checkInDate ? formData.checkInDate.add(1, 'day') : today.add(1, 'day')),
    [formData.checkInDate, today]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.propertyId) {
      setErrorMessage('Please select a property.');
      setStatus('error');
      return;
    }
    if (!formData.checkInDate || !formData.checkOutDate) {
      setErrorMessage('Please select both check-in and check-out dates.');
      setStatus('error');
      return;
    }
    if (formData.checkOutDate.isBefore(formData.checkInDate) || formData.checkOutDate.isSame(formData.checkInDate)) {
      setErrorMessage('Check-out date must be after check-in date.');
      setStatus('error');
      return;
    }
    if (!formData.numberOfRooms || parseInt(formData.numberOfRooms, 10) < 1) {
      setErrorMessage('Please enter at least 1 room.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    const payload = {
      propertyId: formData.propertyId,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      checkInDate: formData.checkInDate.toISOString(),
      checkOutDate: formData.checkOutDate.toISOString(),
      numberOfRooms: parseInt(formData.numberOfRooms, 10),
      roomTypes: formData.roomTypes.length > 0 ? formData.roomTypes : null,
      message: formData.message,
    };

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5095'}/api/leads`, payload);
      setStatus('success');
      setFormData({
        propertyId: '',
        name: '',
        email: '',
        phone: '',
        checkInDate: null,
        checkOutDate: null,
        numberOfRooms: '',
        roomTypes: [],
        message: '',
      });
    } catch (error) {
      console.error('Submission error:', error);
      setStatus('error');
      setErrorMessage(
        error.response?.data?.message || 'An error occurred. Please try again later.'
      );
    }
  };

  const handleCloseSnackbar = () => {
    if (status === 'success' || status === 'error') setStatus('idle');
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={styles.pageBackground}>
          <Container maxWidth="md">
            <Paper elevation={24} sx={styles.card}>
              {/* Decorative gold top bar */}
              <Box sx={styles.goldBar} />

              {/* Header */}
              <Box sx={styles.headerBox}>
                <Typography variant="h4" component="h1" gutterBottom color="primary.main">
                  Sales Inquiry
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Experience the luxury. Reach out to our sales team for group bookings, events, or special requests.
                </Typography>
              </Box>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <Box sx={styles.formGrid}>

                  {/* ROW 1: Select Property — full width */}
                  <MemoTextField
                    select
                    fullWidth
                    required
                    label="Select Property"
                    name="propertyId"
                    id="property-select"
                    value={formData.propertyId}
                    onChange={handleChange}
                    sx={styles.fullSpan}
                  >
                    {PROPERTIES.map((prop) => (
                      <MenuItem key={prop.id} value={prop.id}>
                        {prop.name}
                      </MenuItem>
                    ))}
                  </MemoTextField>

                  {/* ROW 2: Name, Email, Phone — 3 columns */}
                  <Box sx={styles.threeCol}>
                    <MemoTextField
                      fullWidth
                      required
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    <MemoTextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <MemoTextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </Box>

                  {/* ROW 3: Check-in, Check-out — 2 columns */}
                  <MemoDatePicker
                    label="Check-in Date *"
                    value={formData.checkInDate}
                    onChange={handleCheckInChange}
                    minDate={today}
                    slotProps={datePickerSlotProps}
                  />
                  <MemoDatePicker
                    label="Check-out Date *"
                    value={formData.checkOutDate}
                    onChange={handleCheckOutChange}
                    minDate={checkOutMinDate}
                    slotProps={datePickerSlotProps}
                  />

                  {/* ROW 4: Number of Rooms, Room Types — 2 columns */}
                  <MemoTextField
                    fullWidth
                    required
                    type="number"
                    label="Number of Rooms"
                    name="numberOfRooms"
                    value={formData.numberOfRooms}
                    onChange={handleChange}
                    inputProps={roomCountInputProps}
                  />

                  <RoomTypeSelect value={formData.roomTypes} onChange={handleRoomTypeChange} />

                  {/* ROW 5: Message — full width */}
                  <MemoTextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message / Special Requests"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    sx={styles.fullSpan}
                  />

                  {/* ROW 6: Submit — full width */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={status === 'submitting'}
                    sx={styles.submitButton}
                  >
                    {status === 'submitting' ? (
                      <>
                        Submitting
                        <CircularProgress size={24} sx={styles.spinner} />
                      </>
                    ) : (
                      'Submit Inquiry'
                    )}
                  </Button>

                </Box>
              </form>
            </Paper>
          </Container>
        </Box>

        {/* Snackbar notifications */}
        <Snackbar
          open={status === 'success' || status === 'error'}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={snackbarAnchorOrigin}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={status === 'success' ? 'success' : 'error'}
            variant="filled"
            sx={styles.alert}
          >
            {status === 'success'
              ? 'Thank you! Your inquiry has been submitted successfully. Please check your email.'
              : errorMessage}
          </Alert>
        </Snackbar>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
